import { useTasksByEmetteur } from "@/hooks/use-tasks";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, Calendar } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Task } from "@shared/schema";

const STATUS_LABELS: Record<string, string> = {
  a_faire: "À faire",
  en_cours: "En cours",
  fait: "Fait",
};

const STATUS_COLORS: Record<string, string> = {
  a_faire: "bg-red-100 text-red-800 border-red-200",
  en_cours: "bg-amber-100 text-amber-800 border-amber-200",
  fait: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const PRIORITY_LABELS: Record<string, string> = {
  "1": "Faible",
  "2": "Moyen",
  "3": "Fort",
};

const PRIORITY_COLORS: Record<string, string> = {
  "1": "text-emerald-600",
  "2": "text-amber-600",
  "3": "text-red-600",
};

export function ActionsLiees({ emetteurType, emetteurId }: { emetteurType: string; emetteurId: string }) {
  const { data: tasks = [], isLoading } = useTasksByEmetteur(emetteurType, emetteurId);

  return (
    <div className="space-y-2" data-testid="bloc-actions-liees">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold text-sm">Actions liées</h3>
        <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune action liée</p>
      ) : (
        <div className="space-y-1.5">
          {tasks.map((task: Task) => (
            <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 border text-sm" data-testid={`action-liee-${task.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{task.titre}</p>
              </div>
              <span className={`text-xs font-medium ${PRIORITY_COLORS[task.priorite] || ""}`}>
                {PRIORITY_LABELS[task.priorite] || ""}
              </span>
              {task.dateEcheance && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(task.dateEcheance + "T00:00:00"), "dd MMM yyyy", { locale: fr })}
                </span>
              )}
              <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[task.status] || ""}`}>
                {STATUS_LABELS[task.status] || task.status}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
